import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { take } from 'rxjs/operators';

import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
} from '../../confirm-dialog/confirm-dialog.component';
import { CommonService } from '../../services/common.service';
import {
  RuleConfigSaveTarget,
  RuleEngineService,
} from '../../services/rule-engine.service';
import { ToastrService } from 'ngx-toastr';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import type { RoutingVersionOption } from '../models/routing-ruleconfig1.models';
import { RoutingRuleconfig1Hl7RulesService } from './routing-ruleconfig1-hl7-rules.service';
import { RoutingRuleconfig1PipelineService } from './routing-ruleconfig1-pipeline.service';
import { RoutingRuleconfig1StoreService } from './routing-ruleconfig1-store.service';

@Injectable()
export class RoutingRuleconfig1VersionService {
  /**
   * Injects store, pipeline, HL7 rules, rule engine, and UI services.
   * @example new RoutingRuleconfig1VersionService(store, pipeline, hl7Rules, ruleEngine, common, toastr, loader)
   */
  constructor(
    private readonly store: RoutingRuleconfig1StoreService,
    private readonly pipeline: RoutingRuleconfig1PipelineService,
    private readonly hl7Rules: RoutingRuleconfig1Hl7RulesService,
    private readonly ruleEngineService: RuleEngineService,
    private readonly commonService: CommonService,
    private readonly toastr: ToastrService,
    private readonly loader: NgxUiLoaderService,
    private readonly dialog: MatDialog
  ) {}

  /**
   * Initializes versions from dialog data or seeds a single Dev version.
   * @example version.init({ ruleConfigVersions: [{ Version: '1', VersionStatus: 'Dev', RuleConfig: '[]' }] })
   */
  init(dialogData: { routingRuleConfig?: unknown[]; ruleConfigVersions?: unknown[] }): void {
    const versions = dialogData.ruleConfigVersions;
    if (Array.isArray(versions) && versions.length) {
      this.applyVersionList(versions);
      return;
    }
    if (dialogData.routingRuleConfig) {
      this.pipeline.initFromConfig(dialogData.routingRuleConfig as unknown[]);
      this.store.versionOptions = [
        {
          value: '1',
          label: '1 Dev',
          status: 'Dev',
          created: '',
          isLatestLive: false,
          isLatestDevQc: true,
          ruleConfig: dialogData.routingRuleConfig,
          routeRuleMappingID: null,
        },
      ];
      this.store.selectedVersion = '1';
      this.updateActionButtonsForStatus('Dev');
    }
  }

  /**
   * Loads createpipeline submenu permissions from session role menus.
   * @example version.setupPageRolePermissions('createpipeline')
   */
  setupPageRolePermissions(componentname?: string): void {
    try {
      const roleMenus = JSON.parse(sessionStorage.getItem('rolewithmenus') || '[]');
      const selectedRole = sessionStorage.getItem('SelectedUserRole');
      if (!roleMenus?.length || !selectedRole) {
        return;
      }
      const roleData = roleMenus.find((x: { RoleName: string }) => x.RoleName === selectedRole);
      if (!roleData?.menuslist) {
        return;
      }

      let moduleName: string | null = null;
      if (componentname === 'interface') {
        moduleName = 'interface';
      } else if (componentname === 'createpipeline') {
        moduleName = 'createpipeline';
      }
      if (!moduleName) {
        return;
      }

      const menuid = roleData.menuslist.find((y: { MenuName: string }) => y.MenuName === moduleName)?.MenuId;
      roleData.menuslist
        .filter((y: { ParentMenuId: unknown }) => y.ParentMenuId === menuid)
        .forEach((element: { MenuName: string }) => {
          this.store.pagerolepermisions[element.MenuName] = true;
        });
    } catch {
      this.store.pagerolepermisions = {};
    }
  }

  /**
   * Saves current rules without closing the dialog (legacy behavior).
   * @example version.onClickSave() // POST InsertAndUpdateRuleConfig with buildPersistedPayload()
   */
  async onClickSave(): Promise<void> {
    const payload = this.store.buildPersistedPayload();
    const saveTarget = this.ruleEngineService.resolveRuleConfigSaveTarget(
      this.store.versionOptions
    );
    if (
      this.ruleEngineService.shouldConfirmDevQcOverwriteOnSave(
        this.store.selectedVersion,
        saveTarget
      ) &&
      !(await this.confirmDevQcOverwriteBeforeSave(saveTarget))
    ) {
      return;
    }
    this.persistRuleConfigSave(payload, saveTarget);
  }

  private async confirmDevQcOverwriteBeforeSave(
    saveTarget: RuleConfigSaveTarget
  ): Promise<boolean> {
    const selectedOption = this.store.versionOptions.find(
      (o) => o.value?.toString() === this.store.selectedVersion?.toString()
    );
    const overwriteLabels = this.ruleEngineService.buildRuleConfigOverwriteConfirmLabels(
      this.store.selectedVersion,
      selectedOption?.displayStatus ?? selectedOption?.status,
      saveTarget.targetVersion,
      saveTarget.targetStatus
    );
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: '480px',
      panelClass: 'rule-config-overwrite-confirm-panel',
      data: new ConfirmDialogModel(
        'Confirm Save',
        '',
        'confirm',
        '',
        overwriteLabels
      ),
      disableClose: true,
    });
    return (await dialogRef.afterClosed().toPromise()) === true;
  }

  private persistRuleConfigSave(
    payload: ReturnType<RoutingRuleconfig1StoreService['buildPersistedPayload']>,
    saveTarget: RuleConfigSaveTarget
  ): void {
    const {
      targetVersion,
      targetStatus,
      isSameStatusUpdate,
      isNewRuleConfigVersion,
    } = saveTarget;

    const body = {
      RouteUniqueId: this.store.uniqueID,
      RuleConfig: JSON.stringify(payload),
      RuleExecutionStage: this.store.ruleExecutionStage,
      Version: targetVersion,
      VersionStatus: targetStatus,
      IsLive: false,
    };

    this.loader.start();
    this.ruleEngineService.InsertAndUpdateRuleConfig(body).subscribe({
      next: () => {
        this.store.latestRuleExpressions = payload;
        if (isSameStatusUpdate) {
          this.commonService
            .auditsave(
              'Update',
              '',
              `Routing RuleConfig Version is Updated-${targetVersion} ${targetStatus}`,
              'Pipeline',
              this.store.uniqueID,
              'Routing RuleConfig Version Updated Successfully'
            )
            .catch(() => undefined);
        } else if (isNewRuleConfigVersion) {
          this.commonService
            .auditsave(
              'Create',
              '',
              `New RoutingRuleConfig Version is Created-${targetVersion} ${targetStatus}`,
              'Pipeline',
              this.store.uniqueID,
              'Routing RuleConfig Version Created Successfully.'
            )
            .catch(() => undefined);
        }
        this.toastr.success('Routing RuleConfig saved successfully.', '', {
          timeOut: 2000,
          positionClass: 'toast-bottom-right',
        });
        this.loadVersionsFromApi(targetVersion, true);
        this.loader.stop();
      },
      error: () => {
        this.loader.stop();
        this.toastr.error('Unable to save Routing RuleConfig.', '', {
          timeOut: 4000,
          positionClass: 'toast-bottom-right',
        });
      },
    });
  }

  /**
   * Reloads version list from API and applies selection.
   * @example loadVersionsFromApi(2) // selects version '2' when present
   */
  private loadVersionsFromApi(
    preferredVersion?: number | string,
    preserveEditState = false
  ): void {
    if (!this.store.uniqueID) {
      return;
    }
    const reloadOptions = preserveEditState ? { preserveEditState: true as const } : undefined;
    this.ruleEngineService.GetRuleConfigVersions(this.store.uniqueID, this.store.ruleExecutionStage).subscribe({
      next: (data: unknown) =>
        this.applyVersionList(Array.isArray(data) ? data : [], preferredVersion, reloadOptions),
      error: () => this.applyVersionList([], preferredVersion, reloadOptions),
    });
  }

  /**
   * Loads rule config for the selected version into pipeline blocks.
   * @example version.onVersionChange({ value: '3' })
   */
  onVersionChange(
    ev: MatSelectChange | { value?: string },
    options?: { preserveEditState?: boolean }
  ): void {
    const val = (ev?.value ?? this.store.selectedVersion ?? '').toString();
    const opt = this.store.versionOptions.find((o) => o.value === val);
    this.updateActionButtonsForStatus(opt?.status);
    if (!opt?.ruleConfig) {
      this.pipeline.initFromConfig([], options);
      this.restoreHl7FieldMappingsIfNeeded();
      return;
    }
    const extracted = this.extractRoutingRuleConfigFromVersion(opt.ruleConfig);
    if (extracted?.length) {
      this.pipeline.initFromConfig(extracted, options);
      this.restoreHl7FieldMappingsIfNeeded();
      return;
    }
    const parsed = this.parseRuleConfig(opt.ruleConfig);
    if (Array.isArray(parsed) && parsed.length) {
      this.pipeline.initFromConfig(parsed, options);
    } else if (parsed && typeof parsed === 'object') {
      this.pipeline.initFromConfig([parsed], options);
    } else {
      this.pipeline.initFromConfig([], options);
    }
    this.restoreHl7FieldMappingsIfNeeded();
  }

  /**
   * Restores HL7 field display mappings after version-driven config reload.
   * @example restoreHl7FieldMappingsIfNeeded() // when store.isHl7 is true
   */
  private restoreHl7FieldMappingsIfNeeded(): void {
    if (this.store.isHl7) {
      this.hl7Rules.restoreFieldDisplayMappingsFromBlocks(this.store.pipelineBlocks);
      this.store.patchBlocks([...this.store.pipelineBlocks]);
    }
  }

  /**
   * Maps API version rows to store.versionOptions and loads selected config.
   * @example applyVersionList([{ Version: '1', VersionStatus: 'Dev', RuleConfig: '[]' }], '1')
   */
  private applyVersionList(
    raw: unknown[],
    preferredVersion?: number | string,
    options?: { preserveEditState?: boolean }
  ): void {
    const list = raw || [];
    if (!list.length) {
      this.store.versionOptions = [
        {
          value: '1',
          label: '1 Dev',
          status: 'Dev',
          created: '',
          isLatestLive: false,
          isLatestDevQc: true,
          ruleConfig: null,
          routeRuleMappingID: null,
        },
      ];
      this.store.selectedVersion = '1';
      this.updateActionButtonsForStatus('Dev');
      this.pipeline.initFromConfig([], options);
      return;
    }

    let latestLiveIndex = -1;
    let latestDevQcIndex = -1;

    this.store.versionOptions = list.map((item: unknown, idx: number) => {
      const row = item as Record<string, unknown>;
      const version = String(row.Version ?? row.version ?? idx + 1);
      const status = (row.VersionStatus ?? row.status ?? '').toString().trim();
      const createdRaw = row.CreatedDate ?? row.created ?? '';
      const created = this.formatVersionCreatedDate(createdRaw);
      const isLive = !!status && status.toLowerCase() === 'live';
      const isDevOrQc = !!status && (status.toLowerCase() === 'dev' || status.toLowerCase() === 'qc');

      if (isLive) {
        latestLiveIndex = idx;
      } else if (isDevOrQc) {
        latestDevQcIndex = idx;
      }

      return {
        value: version,
        label: '',
        status,
        created,
        isLive,
        isLatestLive: false,
        isLatestDevQc: false,
        displayStatus: undefined,
        ruleConfig: row.RuleConfig ?? row.ruleConfig,
        routeRuleMappingID: (row.RouteRuleMappingID ?? row.routeRuleMappingID) as number | null,
      } as RoutingVersionOption;
    });

    if (latestLiveIndex >= 0) {
      this.store.versionOptions[latestLiveIndex].isLatestLive = true;
    }
    if (latestDevQcIndex >= 0) {
      this.store.versionOptions[latestDevQcIndex].isLatestDevQc = true;
    }

    for (const opt of this.store.versionOptions) {
      if (opt.isLive && !opt.isLatestLive) {
        opt.displayStatus = 'Archived';
      }
      const statusText = (opt.displayStatus ?? opt.status ?? '').toString().trim();
      const dateText = opt.created ? ` (${opt.created})` : '';
      opt.label = `${opt.value}${statusText ? ' ' + statusText : ''}${dateText}`.trim();
    }

    const preferredOpt =
      preferredVersion != null
        ? this.store.versionOptions.find((o) => o.value === String(preferredVersion))
        : null;
    const selected =
      preferredOpt ??
      (latestLiveIndex >= 0
        ? this.store.versionOptions[latestLiveIndex]
        : this.store.versionOptions[this.store.versionOptions.length - 1]);

    this.store.selectedVersion = selected?.value ?? null;
    this.updateActionButtonsForStatus(selected?.status);
    this.onVersionChange({ value: this.store.selectedVersion ?? undefined }, options);
  }

  /**
   * Formats API created date for version dropdown labels.
   * @example formatVersionCreatedDate('2024-06-01T10:00:00') // '2024-06-01 10:00:00'
   */
  private formatVersionCreatedDate(createdRaw: unknown): string {
    return this.commonService.formatRuleConfigCreatedDateForDisplay(createdRaw);
  }

  /**
   * Extracts routing-shaped rule config array from a version payload.
   * @example extractRoutingRuleConfigFromVersion('[{ pipeline: "id", rules: [] }]') // [{ pipeline, rules }]
   */
  private extractRoutingRuleConfigFromVersion(configData: unknown): unknown[] | null {
    const parsed = this.parseRuleConfig(configData);
    if (!parsed) {
      return null;
    }
    const asArray = Array.isArray(parsed) ? parsed : [parsed];
    if (!asArray.length) {
      return null;
    }
    const first = asArray[0] as { rules?: unknown[] };
    if (!first?.rules) {
      return null;
    }
    return asArray;
  }

  /**
   * Parses RuleConfig JSON string or nested RuleConfig object.
   * @example parseRuleConfig('{"RuleConfig":"[{\\"rules\\":[]}]"}') // parsed array or object
   */
  private parseRuleConfig(configData: unknown): unknown {
    try {
      let data = configData;
      if (typeof data === 'string') {
        data = JSON.parse(data);
      }
      if (data && typeof data === 'object' && (data as Record<string, unknown>)['RuleConfig']) {
        const nested = (data as Record<string, unknown>)['RuleConfig'];
        return typeof nested === 'string' ? JSON.parse(nested) : nested;
      }
      return data;
    } catch {
      return null;
    }
  }

  /**
   * Toggles Send to QC / Approve-Reject buttons from version status.
   * @example updateActionButtonsForStatus('QC') // isApproveReject true, isSendToQc false
   */
  updateActionButtonsForStatus(status?: string): void {
    const s = (status ?? '').toString().trim().toLowerCase();
    if (s === 'dev') {
      this.store.isSendToQc = true;
      this.store.isApproveReject = false;
    } else if (s === 'qc') {
      this.store.isSendToQc = false;
      this.store.isApproveReject = true;
    } else {
      this.store.isSendToQc = false;
      this.store.isApproveReject = false;
    }
  }

  /**
   * Opens QC note dialog for Send to QC workflow.
   * @example version.onClickSendToQC() // store.qcStage = 'qc', store.showQcNote = true
   */
  onClickSendToQC(): void {
    this.store.qcStage = 'qc';
    this.store.qcNoteContent = '';
    this.loadVersionNotes(() => {
      this.store.showQcNote = true;
    });
  }

  /**
   * Opens QC note dialog for manager approve workflow.
   * @example version.onClickApprove() // store.qcStage = 'approve'
   */
  onClickApprove(): void {
    this.store.qcStage = 'approve';
    this.store.qcNoteContent = '';
    this.loadVersionNotes(() => {
      this.store.showQcNote = true;
    });
  }

  /**
   * Opens QC note dialog for manager reject workflow.
   * @example version.onClickReject() // store.qcStage = 'reject'
   */
  onClickReject(): void {
    this.store.qcStage = 'reject';
    this.store.qcNoteContent = '';
    this.loadVersionNotes(() => {
      this.store.showQcNote = true;
    });
  }

  /**
   * Closes the QC note overlay and clears qcStage.
   * @example version.closeQcNote()
   */
  closeQcNote(): void {
    this.store.showQcNote = false;
    this.store.qcStage = null;
  }

  /**
   * Opens version notes panel and loads audit notes from API.
   * @example version.openVersionNotesBox()
   */
  openVersionNotesBox(): void {
    this.store.showVersionNotes = true;
    this.store.notesLoading = true;
    this.loadVersionNotes(() => {
      this.store.notesLoading = false;
    });
  }

  /**
   * Hides the version notes panel.
   * @example version.closeVersionNotesBox()
   */
  closeVersionNotesBox(): void {
    this.store.showVersionNotes = false;
  }

  /**
   * Opens version compare overlay with default left/right versions.
   * @example version.onClickCompare() // store.showCompareOverlay = true
   */
  onClickCompare(): void {
    this.store.showCompareOverlay = true;
    const pair = this.ruleEngineService.getDefaultCompareVersions(this.store.versionOptions);
    this.store.compareLeftVersion = pair.left;
    this.store.compareRightVersion = pair.right;
    this.refreshCompareDiff();
  }

  /**
   * Closes the version compare overlay.
   * @example version.closeCompareOverlay()
   */
  closeCompareOverlay(): void {
    this.store.showCompareOverlay = false;
  }

  /**
   * Refreshes monaco diff models when compare version selectors change.
   * @example version.onCompareVersionChange()
   */
  onCompareVersionChange(): void {
    this.refreshCompareDiff();
  }

  /**
   * Persists QC/approve/reject note and updates version status when applicable.
   * @example version.saveRuleVersionNote() // after store.qcStage is set and note entered
   */
  saveRuleVersionNote(): void {
    const payload = this.store.latestRuleExpressions ?? this.store.buildPersistedPayload();
    if (!payload) {
      this.toastr.warning('Please save rules first.', '', {
        timeOut: 3000,
        positionClass: 'toast-bottom-right',
      });
      return;
    }

    const lastVersionValue = this.store.versionOptions.length
      ? parseInt(this.store.versionOptions[this.store.versionOptions.length - 1].value, 10)
      : 1;
    const currentNumeric = parseInt(this.store.selectedVersion as string, 10) || lastVersionValue;

    if (this.store.qcStage === 'qc') {
      this.updateRuleConfigAndSaveNote({
        payload,
        version: currentNumeric,
        versionStatus: 'QC',
        role: 'QC',
        errorMessage: 'Unable to update version to QC.',
        onAfterNoteSaved: () => {
          this.addAuditEntry('QC', this.store.qcNoteContent || '(no note)');
          this.commonService
            .auditsave(
              'Update',
              '',
              `Routing RuleConfig qc approval is completed for version-${currentNumeric} QC`,
              'Pipeline',
              this.store.uniqueID,
              'Routing RuleConfig Version Status Updated Successfully'
            )
            .catch(() => undefined);
          this.refreshVersionsAfterSave(currentNumeric, {
            successMessage: 'Sent to QC Verification.',
            errorMessage: 'Sent to QC, but unable to refresh versions.',
            isApproveReject: true,
            isSendToQc: false,
          });
        },
      });
      return;
    }

    const role = this.store.qcStage === 'approve' ? 'Manager' : 'Manager (Rejected)';

    this.addAuditEntry(role, this.store.qcNoteContent || '(no note)');

    if (this.store.qcStage === 'approve') {
      this.updateRuleConfigAndSaveNote({
        payload,
        version: currentNumeric,
        versionStatus: 'Live',
        role: 'Manager',
        errorMessage: 'Unable to approve and make version Live. Please try again.',
        onAfterNoteSaved: () => {
          this.commonService
            .auditsave(
              'Update',
              '',
              `Routing RuleConfig is approved by manager for version-${currentNumeric} Live`,
              'Pipeline',
              this.store.uniqueID,
              'Routing RuleConfig Version Status is updated to Live Successfully'
            )
            .catch(() => undefined);
          this.refreshVersionsAfterSave(currentNumeric, {
            successMessage: 'Rule version is approved and made live successfully.',
            errorMessage: 'Approved to Live, but unable to refresh versions.',
            isApproveReject: true,
            isSendToQc: false,
          });
        },
      });
    } else {
      this.saveCurrentVersionNoteToServer('Manager', () => {
        const rejectedOption = this.getSelectedVersionOption();
        const rejectedStatus = (rejectedOption?.status ?? 'QC').toString().trim();
        this.commonService
          .auditsave(
            'Update',
            '',
            `Routing RuleConfig is rejected by manager for version-${currentNumeric} ${rejectedStatus}`,
            'Pipeline',
            this.store.uniqueID,
            'Routing RuleConfig Version Updated Successfully'
          )
          .catch(() => undefined);
        this.store.latestRuleExpressions = payload;
        this.store.isApproveReject = false;
        this.store.showQcNote = false;
        this.store.qcStage = null;
        this.toastr.success('Reject note saved successfully.', '', {
          timeOut: 2000,
          positionClass: 'toast-bottom-right',
        });
      });
    }
  }

  /**
   * Returns the version option matching store.selectedVersion.
   * @example getSelectedVersionOption() // { value: '2', status: 'QC', routeRuleMappingID: 10, ... }
   */
  private getSelectedVersionOption(): RoutingVersionOption | undefined {
    if (this.store.selectedVersion == null) {
      return undefined;
    }
    return this.store.versionOptions.find(
      (o) => o.value?.toString() === this.store.selectedVersion?.toString()
    );
  }

  /**
   * Prepends a local QC audit row for the current user and timestamp.
   * @example addAuditEntry('QC', 'Looks good')
   */
  private addAuditEntry(role: string, notes: string): void {
    const user =
      JSON.parse(sessionStorage.getItem('sessionObjectlogin') || 'null')?.userdata?.userid || 'current.user';
    const timestamp = new Date().toLocaleString();
    this.store.qcAudit.unshift({ role, notes, user, timestamp });
  }

  /**
   * POSTs version note to InsertRuleConfigVersionNotes for the selected version.
   * @example saveCurrentVersionNoteToServer('Manager', () => loader.stop())
   */
  private saveCurrentVersionNoteToServer(role: string, onCompleted: () => void): void {
    const option = this.getSelectedVersionOption();
    const routeRuleMappingID = option?.routeRuleMappingID;
    const userID = JSON.parse(sessionStorage.getItem('sessionObjectlogin') || 'null')?.userdata?.userid;

    if (routeRuleMappingID == null) {
      onCompleted();
      return;
    }

    const notesBody = {
      RouteUniqueID: Number(this.store.uniqueID),
      RouteRuleMappingID: Number(routeRuleMappingID),
      Notes: this.store.qcNoteContent || null,
      UserID: userID,
      Role: role,
      CreatedDate: new Date().toISOString(),
    };

    this.ruleEngineService.InsertRuleConfigVersionNotes(notesBody).subscribe({
      next: () => onCompleted(),
      error: () => onCompleted(),
    });
  }

  /**
   * Updates rule config status then saves version note and runs callback.
   * @example updateRuleConfigAndSaveNote({ payload: [], version: 2, versionStatus: 'QC', role: 'QC', onAfterNoteSaved: () => {} })
   */
  private updateRuleConfigAndSaveNote(params: {
    payload: unknown[];
    version: number;
    versionStatus: string;
    role: string;
    onAfterNoteSaved: () => void;
    errorMessage?: string;
  }): void {
    const { payload, version, versionStatus, role, onAfterNoteSaved, errorMessage } = params;
    const body = {
      RouteUniqueId: this.store.uniqueID,
      RuleConfig: JSON.stringify(payload),
      RuleExecutionStage: this.store.ruleExecutionStage,
      Version: version,
      VersionStatus: versionStatus,
    };
    this.loader.start();
    this.ruleEngineService.InsertAndUpdateRuleConfig(body).subscribe({
      next: () => {
        this.store.latestRuleExpressions = payload;
        this.saveCurrentVersionNoteToServer(role, onAfterNoteSaved);
      },
      error: () => {
        this.loader.stop();
        this.toastr.error(errorMessage || 'Unable to update RuleConfig.', '', {
          timeOut: 4000,
          positionClass: 'toast-bottom-right',
        });
      },
    });
  }

  /**
   * Reloads versions after QC/approve and shows success or error toast.
   * @example refreshVersionsAfterSave(2, { successMessage: 'Sent to QC Verification.', isSendToQc: false })
   */
  private refreshVersionsAfterSave(
    currentNumeric: number,
    options?: {
      successMessage?: string;
      errorMessage?: string;
      isApproveReject?: boolean;
      isSendToQc?: boolean;
    }
  ): void {
    const opts = options || {};
    const successMessage = opts.successMessage ?? 'RuleConfig approved and moved to QC.';
    const errorMessage = opts.errorMessage ?? 'Unable to refresh versions.';
    const isApproveReject = opts.isApproveReject ?? false;
    const isSendToQc = opts.isSendToQc ?? false;

    this.ruleEngineService.GetRuleConfigVersions(this.store.uniqueID, this.store.ruleExecutionStage).subscribe({
      next: (data: unknown) => {
        const raw = Array.isArray(data) ? data : [];
        this.applyVersionList(raw, currentNumeric);
        this.loader.stop();
        this.toastr.success(successMessage, '', {
          timeOut: 2000,
          positionClass: 'toast-bottom-right',
        });
        this.store.isApproveReject = isApproveReject;
        this.store.isSendToQc = isSendToQc;
        this.store.showQcNote = false;
        this.store.qcStage = null;
      },
      error: () => {
        this.loader.stop();
        this.toastr.error(errorMessage, '', {
          timeOut: 3000,
          positionClass: 'toast-bottom-right',
        });
        this.store.isApproveReject = isApproveReject;
        this.store.isSendToQc = isSendToQc;
        this.store.showQcNote = false;
        this.store.qcStage = null;
      },
    });
  }

  /**
   * Loads JSON strings for left/right compare versions into monaco models.
   * @example refreshCompareDiff() // uses store.compareLeftVersion and compareRightVersion
   */
  private refreshCompareDiff(): void {
    const leftJson = this.ruleEngineService.getRuleConfigJsonForVersionFromOptions(
      this.store.versionOptions,
      this.store.compareLeftVersion,
      'ruleConfig'
    );
    const rightJson = this.ruleEngineService.getRuleConfigJsonForVersionFromOptions(
      this.store.versionOptions,
      this.store.compareRightVersion,
      'ruleConfig'
    );
    this.store.compareDiffOldModel = { ...this.store.compareDiffOldModel, code: leftJson };
    this.store.compareDiffNewModel = { ...this.store.compareDiffNewModel, code: rightJson };
  }

  /**
   * Fetches version notes from API into store.qcAudit.
   * @example loadVersionNotes(() => { store.notesLoading = false; })
   */
  private loadVersionNotes(onDone?: () => void): void {
    const option = this.store.versionOptions.find((o) => o.value === this.store.selectedVersion);
    const routeRuleMappingID = option?.routeRuleMappingID;
    if (routeRuleMappingID == null) {
      this.store.qcAudit = [];
      onDone?.();
      return;
    }
    this.ruleEngineService
      .GetRuleConfigVersionNotes({ routeUniqueID: this.store.uniqueID, routeRuleMappingID })
      .subscribe({
        next: (data: unknown) => {
          const list = Array.isArray(data) ? data : [];
          this.store.qcAudit = list.map((n: Record<string, unknown>) => {
            const rawTs = n.CreatedDate ?? '';
            const timestamp = this.commonService.formatRuleConfigCreatedDateForDisplay(rawTs) || `${rawTs}`;
            return {
              role: (n.Role ?? '').toString(),
              notes: (n.Notes ?? '').toString(),
              user: (n.UserID ?? '').toString(),
              timestamp,
            };
          });
          onDone?.();
        },
        error: () => onDone?.(),
      });
  }
}
